import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_NAMES = {
  '/': 'لوحة التحكم',
  '/clients': 'العملاء',
  '/operations': 'اللوحة المالية',
  '/employees': 'المنشآت والموظفين',
  '/reports': 'التقارير',
  '/tracking': 'متابعة المعاملات',
  '/work-plans': 'خطط العمل',
  '/users': 'المستخدمين',
  '/activity': 'سجل النشاط',
};

const getPageName = (pathname) => {
  if (PAGE_NAMES[pathname]) return PAGE_NAMES[pathname];
  if (pathname.startsWith('/employees/')) return 'تفاصيل موظف';
  if (pathname.startsWith('/operations/')) return 'عمليات عميل';
  return pathname;
};

const getSessionId = () => {
  let sid = sessionStorage.getItem('activity_sid');
  if (!sid) {
    sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('activity_sid', sid);
  }
  return sid;
};

export const ActivityTracker = () => {
  const location = useLocation();
  const { user, token } = useAuth();
  const lastPage = useRef('');
  const heartbeatRef = useRef(null);
  const sessionId = useRef(getSessionId());

  const logEvent = async (eventType, page, details = '') => {
    if (!token) return;
    try {
      await axios.post(`${API}/activity/event`, { event_type: eventType, page, details });
    } catch {}
  };

  const sendHeartbeat = async () => {
    if (!token) return;
    try {
      await axios.post(`${API}/activity/heartbeat`, {
        session_id: sessionId.current,
        page: getPageName(location.pathname),
      });
    } catch {}
  };

  // Track page navigation
  useEffect(() => {
    if (!user) return;
    const pageName = getPageName(location.pathname);
    if (pageName !== lastPage.current) {
      lastPage.current = pageName;
      logEvent('page_view', pageName);
    }
  }, [location.pathname, user]);

  // Heartbeat every 60 seconds
  useEffect(() => {
    if (!user) return;
    sendHeartbeat();
    heartbeatRef.current = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(heartbeatRef.current);
  }, [user]);

  // End session on unload
  useEffect(() => {
    if (!user) return;
    const handleUnload = () => {
      const url = `${API}/activity/end-session`;
      const body = JSON.stringify({ session_id: sessionId.current });
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [user]);

  return null;
};

// Helper to log file downloads from anywhere
export const logFileDownload = async (fileName, context = '') => {
  try {
    await axios.post(`${API}/activity/event`, {
      event_type: 'file_download',
      page: context || window.location.pathname,
      details: fileName,
    });
  } catch {}
};
